import { type ReactNode } from 'react'
import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router'
import { type ColumnDef } from '@tanstack/react-table'
import { CalendarClock, ChevronRight, User } from 'lucide-react'
import { z } from 'zod'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/ui/data-table'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { StageTransitionControl } from '@/components/pipeline/stage-transition'
import {
  usePipelineActivity,
  usePipelineAttachments,
  usePipelineDeal,
  usePipelineLineItems,
  usePipelineNotes,
  usePipelineTasks,
} from '@/hooks/use-pipeline'
import {
  EXPIRING_SOON_DAYS,
  GOAL_LABELS,
  SOURCE_LABELS,
  daysUntil,
  formatCurrency,
  localDateISO,
} from '@/lib/pipeline'
import { cn, relativeTime } from '@/lib/utils'

const DETAIL_TABS = ['overview', 'line_items', 'attachments', 'tasks', 'notes', 'activity'] as const
type DetailTab = (typeof DETAIL_TABS)[number]

const TAB_LABELS: Record<DetailTab, string> = {
  overview: 'Overview',
  line_items: 'Line Items',
  attachments: 'Attachments',
  tasks: 'Tasks',
  notes: 'Notes',
  activity: 'Activity',
}

const detailSearch = z.object({
  tab: z.enum(DETAIL_TABS).optional().default('overview'),
})

export const Route = createFileRoute('/_authed/pipeline/$id')({
  validateSearch: detailSearch,
  component: PipelineDetailPage,
})

type DealDetail = NonNullable<ReturnType<typeof usePipelineDeal>['data']>
type LineItemRow = NonNullable<ReturnType<typeof usePipelineLineItems>['data']>[number]
type AttachmentRow = NonNullable<ReturnType<typeof usePipelineAttachments>['data']>[number]
type TaskRow = NonNullable<ReturnType<typeof usePipelineTasks>['data']>[number]
type ActivityRow = NonNullable<ReturnType<typeof usePipelineActivity>['data']>[number]

const QUOTE_TYPE_LABELS: Record<string, string> = { ams: 'AMS', hardware: 'Hardware' }
const QUOTE_SOURCE_LABELS: Record<string, string> = { axis_import: 'AXIS import', manual: 'Manual' }

const FILE_TYPE_LABELS: Record<string, string> = {
  pdf_quote: 'PDF quote',
  ams_spreadsheet: 'AMS spreadsheet',
  bom: 'BOM',
  contract: 'Contract',
  email: 'Email',
  image: 'Image',
  other: 'Other',
}

const TASK_TYPE_LABELS: Record<string, string> = {
  follow_up: 'Follow Up',
  renewal_alert: 'Renewal Alert',
  bom_followup: 'BOM Follow-up',
  call: 'Call',
  email: 'Email',
  revisit: 'Revisit',
  other: 'Other',
}

const TASK_STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  in_progress: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

function fmtDate(dateStr: string | null): string {
  if (!dateStr) return '—'
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** Line-item money keeps cents; formatCurrency's whole dollars are for deal totals. */
function fmtMoney(amount: number | null | undefined): string {
  if (amount == null) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
}

/** product_categories is untyped Json in the schema; render only string entries. */
function categoryList(value: DealDetail['product_categories']): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []
}

function PipelineDetailPage() {
  const { id } = Route.useParams()
  const { tab } = useSearch({ from: '/_authed/pipeline/$id' })
  const navigate = useNavigate({ from: Route.fullPath })
  const { data: deal, isLoading, error } = usePipelineDeal(id)

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (error || !deal) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>
            Deal not found.{' '}
            <Link to="/pipeline" className="underline">Back to pipeline</Link>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <DealDetailHeader deal={deal} />
      <Tabs
        value={tab}
        onValueChange={(v) =>
          void navigate({ to: '/pipeline/$id', params: { id }, search: (prev) => ({ ...prev, tab: v as DetailTab }) })
        }
        className="flex-1 flex flex-col overflow-hidden"
      >
        <div className="border-b shrink-0">
          <TabsList className="h-10 w-full justify-start rounded-none bg-transparent p-0 gap-0 px-6">
            {DETAIL_TABS.map((t) => (
              <TabsTrigger
                key={t}
                value={t}
                className="rounded-none border-b-2 border-transparent px-4 h-10 data-[state=active]:border-[color:var(--accent-text)] data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              >
                {TAB_LABELS[t]}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="flex-1 overflow-auto">
          <TabsContent value="overview" className="mt-0 p-6">
            <OverviewTab deal={deal} />
          </TabsContent>
          <TabsContent value="line_items" className="mt-0 p-6">
            <LineItemsTab dealId={id} />
          </TabsContent>
          <TabsContent value="attachments" className="mt-0 p-6">
            <AttachmentsTab dealId={id} />
          </TabsContent>
          <TabsContent value="tasks" className="mt-0 p-6">
            <TasksTab dealId={id} />
          </TabsContent>
          <TabsContent value="notes" className="mt-0 p-6">
            <NotesTab dealId={id} />
          </TabsContent>
          <TabsContent value="activity" className="mt-0 p-6">
            <ActivityTab dealId={id} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  )
}

/**
 * Fixed header: everything a rep needs without scrolling. The stage badge is
 * the same StageTransitionControl the list and board use, so every stage move
 * routes through the one validated machine.
 */
function DealDetailHeader({ deal }: { deal: DealDetail }) {
  const title = deal.end_user?.name ?? deal.reseller?.name ?? 'Deal'
  const today = localDateISO(new Date())
  const expiresIn = deal.expiration_date ? daysUntil(deal.expiration_date, today) : null

  return (
    <>
      <nav
        aria-label="Breadcrumb"
        className="flex items-center gap-1 px-6 h-8 text-xs text-[color:var(--ink-3)] bg-[color:var(--surface)] border-b border-[color:var(--line)]"
      >
        <Link to="/pipeline" className="hover:underline">Pipeline</Link>
        <ChevronRight className="h-3 w-3" aria-hidden="true" />
        <span className="truncate">{title}</span>
      </nav>

      <div className="h-12 border-b border-[color:var(--line)] bg-[color:var(--surface)] px-6 flex items-center gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <h2 className="text-[18px] font-[560] leading-6 tracking-[-0.012em] truncate shrink-0 max-w-[30%]">
            {title}
          </h2>
          <StageTransitionControl id={deal.id} stage={deal.stage} />
          <div className="flex items-center gap-3 overflow-hidden text-[13px]">
            <span className="text-[color:var(--ink-3)] truncate">
              {deal.reseller?.name ?? '—'}
              {deal.division ? ` · ${deal.division.name}` : ''}
            </span>
            {deal.quote_ref && (
              <span className="font-mono text-[0.92em] text-[color:var(--ink-3)] shrink-0">
                {deal.quote_ref}
              </span>
            )}
            <span className="tabular-nums [font-feature-settings:'tnum'_1] shrink-0">
              {formatCurrency(deal.total_amount)}
            </span>
            {deal.expiration_date && expiresIn !== null && (
              <span
                className={cn(
                  'flex items-center gap-1 whitespace-nowrap shrink-0',
                  expiresIn < EXPIRING_SOON_DAYS
                    ? 'text-destructive font-medium'
                    : 'text-[color:var(--ink-3)]',
                )}
                title={`Quote expires ${fmtDate(deal.expiration_date)}`}
              >
                <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
                {fmtDate(deal.expiration_date)}
              </span>
            )}
            <span className="flex items-center gap-1 text-[color:var(--ink-3)] min-w-0">
              <User className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">{deal.rep?.full_name ?? '—'}</span>
            </span>
          </div>
        </div>
      </div>
    </>
  )
}

function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-32 shrink-0 pt-0.5 text-[13px] text-[color:var(--ink-3)]">{label}</span>
      <div className="flex-1 min-w-0 text-[13px]">{children}</div>
    </div>
  )
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-[1px] bg-[color:var(--line)]" aria-hidden="true" />
      <h3 className="text-[14px] font-[560] leading-5 tracking-[-0.005em] text-[color:var(--ink)] whitespace-nowrap shrink-0">
        {children}
      </h3>
      <div className="flex-1 h-[1px] bg-[color:var(--line)]" aria-hidden="true" />
    </div>
  )
}

function OverviewTab({ deal }: { deal: DealDetail }) {
  const categories = categoryList(deal.product_categories)
  const contact = deal.reseller_contact
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
      <div className="space-y-4">
        <SectionHeading>Deal</SectionHeading>
        <FieldRow label="Goal">{deal.goal ? GOAL_LABELS[deal.goal] : '—'}</FieldRow>
        <FieldRow label="Source">{deal.source ? SOURCE_LABELS[deal.source] : '—'}</FieldRow>
        <FieldRow label="Quote type">
          {deal.quote_type ? (QUOTE_TYPE_LABELS[deal.quote_type] ?? deal.quote_type) : '—'}
        </FieldRow>
        <FieldRow label="Quote source">
          {deal.quote_source ? (QUOTE_SOURCE_LABELS[deal.quote_source] ?? deal.quote_source) : '—'}
        </FieldRow>
        <FieldRow label="Quote ref">
          <span className="font-mono text-[0.92em]">{deal.quote_ref ?? '—'}</span>
        </FieldRow>
        <FieldRow label="Ref number">
          <span className="font-mono text-[0.92em]">{deal.ref_number ?? '—'}</span>
        </FieldRow>
        <FieldRow label="Reseller Cust ID">
          <span className="font-mono text-[0.92em]">{deal.reseller_cust_id ?? '—'}</span>
        </FieldRow>
        <FieldRow label="Categories">
          {categories.length ? (
            <span className="flex flex-wrap gap-1">
              {categories.map((c) => (
                <Badge key={c} variant="secondary" className="font-normal text-xs">{c}</Badge>
              ))}
            </span>
          ) : (
            '—'
          )}
        </FieldRow>
        <FieldRow label="Reseller contact">
          {contact ? `${contact.first_name} ${contact.last_name}` : '—'}
        </FieldRow>
        <FieldRow label="Solutions architect">{deal.solutions_architect?.full_name ?? '—'}</FieldRow>
        <FieldRow label="Distributor">{deal.distributor?.name ?? '—'}</FieldRow>
      </div>

      <div className="space-y-4">
        <SectionHeading>Dates &amp; amounts</SectionHeading>
        <FieldRow label="Created">{fmtDate(deal.create_date)}</FieldRow>
        <FieldRow label="Submitted">{fmtDate(deal.submit_date)}</FieldRow>
        <FieldRow label="Target quote">{fmtDate(deal.target_quote_date)}</FieldRow>
        <FieldRow label="Target close">{fmtDate(deal.target_close_date)}</FieldRow>
        <FieldRow label="Expiration">{fmtDate(deal.expiration_date)}</FieldRow>
        <FieldRow label="Actual close">{fmtDate(deal.actual_close_date)}</FieldRow>
        {deal.stage === 'on_hold' && (
          <FieldRow label="Revisit">{fmtDate(deal.revisit_date)}</FieldRow>
        )}
        <FieldRow label="Subtotal">
          <span className="tabular-nums [font-feature-settings:'tnum'_1]">{formatCurrency(deal.subtotal)}</span>
        </FieldRow>
        <FieldRow label="Freight">
          <span className="tabular-nums [font-feature-settings:'tnum'_1]">{formatCurrency(deal.freight_amount)}</span>
        </FieldRow>
        <FieldRow label="Total">
          <span className="font-medium tabular-nums [font-feature-settings:'tnum'_1]">{formatCurrency(deal.total_amount)}</span>
        </FieldRow>
      </div>

      {deal.stage === 'on_hold' && deal.on_hold_reason && (
        <div className="md:col-span-2 space-y-2">
          <SectionHeading>On hold</SectionHeading>
          <p className="text-[13px] whitespace-pre-wrap">{deal.on_hold_reason}</p>
        </div>
      )}

      {deal.stage === 'lost' && (deal.loss_reason || deal.loss_notes) && (
        <div className="md:col-span-2 space-y-2">
          <SectionHeading>Loss</SectionHeading>
          {deal.loss_reason && (
            <FieldRow label="Reason">{deal.loss_reason.label}</FieldRow>
          )}
          {deal.loss_notes && (
            <FieldRow label="Notes">
              <span className="whitespace-pre-wrap">{deal.loss_notes}</span>
            </FieldRow>
          )}
        </div>
      )}

      {deal.notes && (
        <div className="md:col-span-2 space-y-2">
          <SectionHeading>Deal notes</SectionHeading>
          <p className="text-[13px] whitespace-pre-wrap">{deal.notes}</p>
        </div>
      )}

      <div className="md:col-span-2 border-t pt-3 flex gap-6 text-xs text-[color:var(--ink-3)]">
        <span>Created {fmtDateTime(deal.created_at)}</span>
        <span>Updated {relativeTime(deal.updated_at)}</span>
      </div>
    </div>
  )
}

function LineItemsTab({ dealId }: { dealId: string }) {
  const { data: items = [], isLoading } = usePipelineLineItems(dealId)

  const columns: ColumnDef<LineItemRow>[] = [
    {
      accessorKey: 'vendor_part_number',
      header: 'Part #',
      cell: ({ row }) => (
        <span className="font-mono text-[0.92em] whitespace-nowrap">{row.original.vendor_part_number}</span>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => (
        <div className="min-w-0 max-w-[360px]">
          <span className="block truncate text-[13px]">{row.original.description ?? '—'}</span>
          {(row.original.master_category || row.original.product_line) && (
            <span className="block truncate text-[11px] text-[color:var(--ink-3)]">
              {[row.original.master_category, row.original.product_line].filter(Boolean).join(' · ')}
            </span>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'quantity',
      header: () => <div className="text-right">Qty</div>,
      cell: ({ row }) => <div className="text-right tabular-nums">{row.original.quantity}</div>,
    },
    {
      accessorKey: 'unit_price',
      header: () => <div className="text-right">Unit price</div>,
      cell: ({ row }) => (
        <div className="text-right tabular-nums [font-feature-settings:'tnum'_1] whitespace-nowrap">
          {fmtMoney(row.original.unit_price)}
        </div>
      ),
    },
    {
      accessorKey: 'ext_amount',
      header: () => <div className="text-right">Ext amount</div>,
      cell: ({ row }) => (
        <div className="text-right font-medium tabular-nums [font-feature-settings:'tnum'_1] whitespace-nowrap">
          {fmtMoney(row.original.ext_amount)}
        </div>
      ),
    },
    {
      accessorKey: 'sla_tier',
      header: 'SLA',
      cell: ({ row }) => <span className="text-[13px] whitespace-nowrap">{row.original.sla_tier ?? '—'}</span>,
    },
    {
      id: 'contract',
      header: 'Contract',
      cell: ({ row }) => {
        const { contract_start_date: start, contract_end_date: end, contract_term: term } = row.original
        if (start || end) {
          return (
            <span className="text-[12px] whitespace-nowrap">
              {fmtDate(start)} – {fmtDate(end)}
            </span>
          )
        }
        return <span className="text-[12px]">{term ?? '—'}</span>
      },
    },
    {
      id: 'flags',
      header: '',
      cell: ({ row }) => (
        <span className="flex gap-1">
          {row.original.is_ncnr && (
            <Badge variant="outline" className="font-normal text-[10px]" title="Non-cancellable, non-returnable">
              NCNR
            </Badge>
          )}
          {row.original.is_tm && (
            <Badge variant="outline" className="font-normal text-[10px]" title="Time & materials">
              T&amp;M
            </Badge>
          )}
        </span>
      ),
    },
  ]

  return (
    <DataTable
      columns={columns}
      data={items}
      loading={isLoading}
      emptyState={
        <EmptyState
          dense
          title="No line items yet"
          description="Line items appear here once they’re added to this deal."
        />
      }
    />
  )
}

function AttachmentsTab({ dealId }: { dealId: string }) {
  const { data: attachments = [], isLoading } = usePipelineAttachments(dealId)

  const columns: ColumnDef<AttachmentRow>[] = [
    {
      accessorKey: 'original_filename',
      header: 'File',
      cell: ({ row }) => (
        <span className="font-medium block truncate max-w-[320px]">{row.original.original_filename}</span>
      ),
    },
    {
      accessorKey: 'file_type',
      header: 'Type',
      cell: ({ row }) => (
        <Badge variant="secondary" className="font-normal text-xs">
          {FILE_TYPE_LABELS[row.original.file_type] ?? row.original.file_type}
        </Badge>
      ),
    },
    {
      accessorKey: 'notes',
      header: 'Notes',
      cell: ({ row }) => (
        <span className="text-[13px] block truncate max-w-[280px]">{row.original.notes ?? '—'}</span>
      ),
    },
    {
      accessorKey: 'uploaded_at',
      header: 'Uploaded',
      cell: ({ row }) => (
        <span className="text-[12px] text-[color:var(--ink-3)] whitespace-nowrap">
          {fmtDateTime(row.original.uploaded_at)}
          {row.original.uploader ? ` · ${row.original.uploader.full_name}` : ''}
        </span>
      ),
    },
  ]

  return (
    <DataTable
      columns={columns}
      data={attachments}
      loading={isLoading}
      emptyState={
        <EmptyState
          dense
          title="No attachments yet"
          description="Files attached to this deal appear here."
        />
      }
    />
  )
}

function TasksTab({ dealId }: { dealId: string }) {
  const { data: tasks = [], isLoading } = usePipelineTasks(dealId)

  const columns: ColumnDef<TaskRow>[] = [
    {
      accessorKey: 'title',
      header: 'Task',
      cell: ({ row }) => (
        <span
          className={cn(
            'font-medium block truncate max-w-[320px]',
            row.original.status === 'completed' && 'line-through text-[color:var(--ink-3)]',
          )}
        >
          {row.original.title}
        </span>
      ),
    },
    {
      accessorKey: 'task_type',
      header: 'Type',
      cell: ({ row }) => (
        <span className="text-[13px]">{TASK_TYPE_LABELS[row.original.task_type] ?? row.original.task_type}</span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant="secondary" className="font-normal text-xs">
          {TASK_STATUS_LABELS[row.original.status] ?? row.original.status}
        </Badge>
      ),
    },
    {
      accessorKey: 'due_date',
      header: 'Due',
      cell: ({ row }) => (
        <span className="text-[13px] whitespace-nowrap">{fmtDate(row.original.due_date)}</span>
      ),
    },
    {
      id: 'assignee',
      header: 'Assigned to',
      cell: ({ row }) => <span className="text-[13px]">{row.original.assignee?.full_name ?? '—'}</span>,
    },
  ]

  return (
    <DataTable
      columns={columns}
      data={tasks}
      loading={isLoading}
      emptyState={
        <EmptyState
          dense
          title="No tasks linked to this deal"
          description="Follow-ups created from this deal appear here."
        />
      }
    />
  )
}

function NotesTab({ dealId }: { dealId: string }) {
  const { data: notes = [], isLoading } = usePipelineNotes(dealId)

  if (isLoading) return <Skeleton className="h-24 w-full" />
  if (notes.length === 0) {
    return (
      <EmptyState
        dense
        title="No notes yet"
        description="Notes added to this deal appear here."
      />
    )
  }

  return (
    <div className="space-y-3 max-w-3xl">
      {notes.map((note) => (
        <div key={note.id} className="rounded-md border border-[color:var(--line)] bg-[color:var(--surface)] px-4 py-3 space-y-1">
          <div className="flex items-center gap-2 text-[12px] text-[color:var(--ink-3)]">
            <span className="font-medium text-[color:var(--ink)]">{note.author?.full_name ?? '—'}</span>
            <span title={fmtDateTime(note.created_at)}>{relativeTime(note.created_at)}</span>
          </div>
          <p className="text-[13px] whitespace-pre-wrap">{note.content}</p>
        </div>
      ))}
    </div>
  )
}

/** Summarize an audit row's `changes` Json as the list of touched fields. */
function changedFields(changes: ActivityRow['changes']): string {
  if (changes && typeof changes === 'object' && !Array.isArray(changes)) {
    return Object.keys(changes).join(', ')
  }
  return '—'
}

function ActivityTab({ dealId }: { dealId: string }) {
  const { data: entries = [], isLoading } = usePipelineActivity(dealId)

  const columns: ColumnDef<ActivityRow>[] = [
    {
      accessorKey: 'occurred_at',
      header: 'When',
      cell: ({ row }) => (
        <span className="text-[12px] text-[color:var(--ink-3)] whitespace-nowrap">
          {fmtDateTime(row.original.occurred_at)}
        </span>
      ),
    },
    {
      accessorKey: 'action',
      header: 'Action',
      cell: ({ row }) => (
        <Badge variant="secondary" className="font-normal text-xs">{row.original.action}</Badge>
      ),
    },
    {
      accessorKey: 'actor_email',
      header: 'By',
      cell: ({ row }) => <span className="text-[13px]">{row.original.actor_email}</span>,
    },
    {
      id: 'changes',
      header: 'Fields changed',
      cell: ({ row }) => (
        <span className="text-[12px] text-[color:var(--ink-3)] block truncate max-w-[320px]">
          {changedFields(row.original.changes)}
        </span>
      ),
    },
  ]

  return (
    <DataTable
      columns={columns}
      data={entries}
      loading={isLoading}
      emptyMessage="No activity visible. Audit history is available to workspace admins."
    />
  )
}
